/**
 * 长图费用预估 —— **单源 `creditRates.ts`**(V1,补设计方案 §2 问题五)。
 *
 * ═══ 为什么这是一个独立模块而不是页内两行算术 ═══════════════════════════════════
 * 长图是舱内最贵的一条路径,却是唯一全程看不见价的:自由创作有 `estimatedCredits`
 * (与后端扣费口径同源),长图搭建器只有一个「N 图块 AI 出图」的角标,卡面 costLabel
 * 干脆写「仅图块计费」—— 说不出多少钱。用户在最贵的路径上反而盲飞。
 *
 * 补价的前提是**只有一个数**:卡面、页内预估、后端实扣三处必须同源。E0 纠偏查出过
 * 「卡面积分标全是假单价、少报 16~69 倍」,那次的根因正是同一个数被抄了三份。
 * 所以这里的每个数字都是算出来的:
 *   费率 ← `creditRates.ts`(平台真值,实测账单钉死)
 *   块数 ← `skeletonFor` / `wideSkeletonFor`(搭建器真骨架,不是另抄一张表)
 * 两个源任意一边改了,`longImageEstimate.test.ts` 必红。
 *
 * ★口径(2026-08-04 起与 ai `_effective_mode` 逐条同镜,见 `blockCallsEngine`):
 * `image` 原语过引擎(photocards 除外);非图块**有统一 DNA 时也过**(智能指派 A/B,
 * 用户拍板过「文字都交给大模型渲染」)—— 没 DNA 才是纯模板渲染不计费。
 * 「默认 8 块 · 3 图块」这个标法要把两个数都写出来的原因:
 * 只写 8 块用户以为要 8 份钱,只写 3 图块用户不知道这张图有多长。
 */
import { LONGIMAGE_ONECLICK_DEFAULT_TIER } from "@/entities/scene-card/model/creditRates";
import type { LongImageUsage, WideLayoutKey } from "@/entities/scene-card/model/presets";
import { LONG_IMAGE_USAGES, WIDE_LAYOUT_KEYS } from "@/entities/scene-card/model/presets";
import type { Primitive } from "../longimage/model/blocks";
import { skeletonFor, wideSkeletonFor } from "../longimage/model/blocks";

/**
 * 只要有 `primitive` 就能算 —— 手动 draft 与 Canvas 稿的块共用这一个入口。
 * `styleTag` / `canvas.renderMode` 是**可选**的:老草稿没有,按下限算(见 engineBlocksOf)。
 * ★`screenAnno`/`originalKeys`(LI-SCREENS-2.0 WP5)同样可选:老草稿/手工搭建器的块
 *   两格皆缺,`screensOf` 据此回落几何区间估算(见该函数头注)。
 */
type CountableBlock = {
  primitive: Primitive;
  styleTag?: string;
  /** ★两格在编辑器态住在 canvas 里(CanvasBlockForm),在 v2 产物里住顶层 ——
   *  LI30(0910)读数统一走 annoOf/originalKeysOf 两个取数器,两处都认。
   *  此前只读顶层 = 编辑器态的「按屏精确报价」「原件零引擎」从没生效过(静默落几何区间,
   *  方向是多报不少扣,所以一直没人被扣冤枉钱,但报价与实扣对不上)。 */
  canvas?: {
    renderMode?: string;
    screenAnno?: { screenNo: number } | null;
    originalKeys?: string[];
  } | null;
  /** 排屏归属标注(镜像 ai canvas.py::BlockScreenAnno,alias screenAnno)。只取 screenNo。 */
  screenAnno?: { screenNo: number } | null;
  /** 原件屏专用槽(镜像 ai LongImageBlock.originals,alias originalKeys)。非空=零引擎。 */
  originalKeys?: string[];
  /** v2 产物顶层 renderMode(编辑器态住在 canvas.renderMode)—— 只有 {@link screensOf} 读顶层。 */
  renderMode?: string;
  /** 用户在结果页删掉的块(payload 里 hidden=true):不在图里,屏数跳过(ai/api 同口径)。 */
  hidden?: boolean;
};

/** 两态取数器(LI30):v2 产物顶层优先,编辑器态回落 canvas 嵌套。 */
function annoOf(b: CountableBlock): { screenNo: number } | null {
  return b.screenAnno ?? b.canvas?.screenAnno ?? null;
}
function originalKeysOf(b: CountableBlock): string[] {
  return b.originalKeys ?? b.canvas?.originalKeys ?? [];
}

/**
 * ★不叫出图引擎的图类(与 ai 侧 `ENGINE_FREE_IMAGE_TAGS` 同一口径,改一边必须改另一边)。
 * photocards 用的是用户实拍 + 矢量文字,一次引擎都不叫,自然也不该报价。
 */
const ENGINE_FREE_IMAGE_TAGS = new Set(["photocards"]);

/**
 * 一键出图钉死 2K(老板 2026-08-20 拍板:直接 2K 出,价写在按钮上,不做 1K 预览档)。
 * ★与 api `LongImageOneClickOrchestrationService.ONECLICK_TIER` 同一个值 ——
 *   那边是真下单用的档,这边是按钮上写的价,两个数必须同源同值。
 */
const ONECLICK_TIER = "2K";

/**
 * 一键路默认分辨率档(批C-2,用户 0823「默认 2K 太贵了」)。
 * ★翻 1K 的硬验收已过(2026-08-23 真机三针,gpt-image-2 @0823j):
 *   12 行密排 numlist 两种 seed + 17 行表格 9:16 极密,1K 全部字形正确、清晰可读、
 *   零缺行(截图存批C报告)。1K 路上有 prompt 侧 clarity directive 双保险在场 ——
 *   这是生产真路径,不是实验室条件。
 * ★搭建器/驾驶舱路仍默认 2K(b.scale=2,ONECLICK_TIER)—— 本常量只管备料页一键路。
 * ★0927 K3:字面量搬到 `entities/scene-card/model/creditRates.LONGIMAGE_ONECLICK_DEFAULT_TIER`
 *   —— 长图用途卡的卡面报价档必须与这里同源(此前卡面写死 2K,报 198 而页内报 28–308、实扣 84),
 *   而 entities 不能反向 import features,所以真值住在 entities,这里是它在 features 侧的名字。
 *   两边相等由 `sceneCardLongImageParity.test.ts` 钉。要改默认档只改那一处。
 */
export const ONECLICK_DEFAULT_TIER: ResolutionTier = LONGIMAGE_ONECLICK_DEFAULT_TIER;

/** 档位 → 提交 scale(tierOfScale 的逆;两边同口径,报价用一套、实扣用另一套是老病)。 */
export function scaleOfTier(tier: ResolutionTier): number {
  return tier === "1K" ? 1 : tier === "4K" ? 3 : 2;
}

export type ResolutionTier = "1K" | "2K" | "4K";

/**
 * 备料页 payload 里带来的 scale → 搭建器合法档(1|2|3);缺/非法 → null(调用方回落现值)。
 * ★1004 T3:智能编排此前不读 payload.scale,备料选 1K 进编排页仍是 2K。
 */
export function scaleFromPayload(scale: unknown): 1 | 2 | 3 | null {
  return scale === 1 || scale === 2 || scale === 3 ? scale : null;
}

/**
 * 搭建器的 `scale` → 提交时的 `resolutionTier`。
 * ★与 `LongImageBuilderPage` 提交侧那行三元同口径 —— 预估用一套换算、实扣用另一套,
 * 就是"卡面与实扣差一个数量级"的老病复发。
 */
export function tierOfScale(scale: number): ResolutionTier {
  return scale === 1 ? "1K" : scale === 3 ? "4K" : "2K";
}

/**
 * ★与 ai `_A_EXCLUDED_TAGS` 同一口径(改一边必须改另一边):二维码要能扫,
 * 是功能红线不是观感 —— ai 侧永远模板渲染,报价自然也永远不计它。
 */
const ENGINE_EXCLUDED_TEXT_TAGS = new Set(["qr"]);

/**
 * 报价谓词的环境:这两个开关都对应 ai 侧**确定性**的分派规则,不是猜。
 */
export interface EngineContext {
  /**
   * 草稿带统一视觉 DNA(styleTokens)吗。带 → ai `_effective_mode` 会把
   * **未指定 renderMode 的非图块**智能指派成 A(文字族)/ B(数据族),都过引擎;
   * 不带 → 一律 C 纯模板。显式 B 无 DNA 也会被 ai 降 C(orchestrator L160)。
   */
  hasStyleTokens?: boolean;
  /**
   * 横宽(banner)吗。ai `_run_wide`:非图块一律 C,**连显式 A/B 都忽略**(D4 注)——
   * 报价同镜,否则横宽稿会朝「多报」的方向错。
   */
  wide?: boolean;
  /**
   * 一键出图(直达模式)吗。ai worker 侧 `oneclick.force_all_a` 把**所有非图块**推到 A
   * (qr 除外),于是块自身的 renderMode 与「有没有 DNA」都不再影响分派 —— 全都过引擎。
   * ★与 `force_all_a` 逐条同镜:改一边必须改另一边(见 longImageEstimate.contract.test.ts)。
   */
  oneClick?: boolean;
  /**
   * 骨架稿吗(canvas.skeleton)。ai `_screens_active`:骨架稿不进屏 —— {@link screensOf}
   * 对它沿用按块口径(与横宽同)。其它谓词不读这一格。
   */
  skeleton?: boolean;
}

/**
 * 区块序列里真正会过引擎出图的块数(2026-08-05 改口径,起因是余额差;
 * 2026-08-04 补智能指派镜像,起因是 A 档软文案块 700/块 vs 预估 0 的口径矛盾)。
 *
 * ai 侧 `_effective_mode` 的实况(与本谓词逐条对应,改那边必须改这边):
 *   · 图块 → IMG 通路(叫引擎),**除了** photocards(实拍 + 矢量字,不叫);
 *   · 非图块 renderMode=A → 全 AI 画(叫引擎,无条件);
 *   · 非图块 renderMode=B → AI 出底板 + 矢量叠字(叫引擎;**无 DNA 被降 C 不叫**);
 *   · 非图块未指定 → 有 DNA 智能指派 A/B(叫,qr 除外);无 DNA → C(不叫);
 *   · 横宽:非图块一律 C(显式 A/B 也忽略)。
 *
 * ★此前未指定按下限报 0 —— 但有 DNA 时指派是**确定性**的,「取下限」实际保证的
 *   恰恰是「报少了还多扣」必然发生。报价的红线方向是**不许少报**,现在按实况算。
 * ★内部改走 {@link blockPassesEngine}(LI-SCREENS-2.0 WP5,`blockCallsEngine` 谓词退役
 *   见该函数头注)——新增原件屏(originalKeys 非空)零引擎口径,老谓词的跨仓契约值不动。
 */
export function engineBlocksOf(blocks: readonly CountableBlock[], ctx: EngineContext = {}): number {
  return blocks.filter((b) => blockPassesEngine(b, ctx)).length;
}

/**
 * 单块版同口径判定(等待页逐块标「AI 出图/模板渲染」用)。
 * ★必须与 engineBlocksOf 是同一个谓词 —— 报价数的块和等待页亮「AI 出图」徽的块
 * 若各写各的,就是「预估 N 图块」和骨架墙徽标数对不上的那种低级自相矛盾。
 *
 * ★退役(LI-SCREENS-2.0 WP5,2026-09-05):**导出保留**只为防外部残留 import 编译炸
 *   (`longImageEstimate.contract.test.ts` 直接 import 它锁一键计费跨仓契约,那张表钉的是
 *   ai `oneclick.force_all_a` 的既有事实,与本批无关,不动)。**新增计算一律不再走它** ——
 *   继任者 {@link blockPassesEngine} 在它之上叠一条「原件屏(originalKeys 非空)零引擎」
 *   (设计定稿§0①:资质/证书图程序化贴放,零 AI 重绘),`engineBlocksOf`/{@link screensOf}
 *   都改吃继任者。两个谓词值域上只在 originalKeys 非空这一种块上分叉,其余逐条相同。
 */
export function blockCallsEngine(b: CountableBlock, ctx: EngineContext = {}): boolean {
  if (b.primitive === "image") return !ENGINE_FREE_IMAGE_TAGS.has(b.styleTag ?? "");
  if (ctx.wide) return false;
  // ★qr 的排除必须**排在 mode 判定之前** —— ai `_effective_mode` 就是这个次序
  // (`style_tag in _A_EXCLUDED_TAGS → "C"` 在读 render_mode 之前)。
  // 2026-08-20 修:此前这条只写在最后那行「未指定」分支里,于是**显式 renderMode="A" 的 qr**
  // 在 console 计费、在 ai 不计费 —— 一条一直在的静默漂移(方向是多报,不是少扣,
  // 所以没人被扣冤枉钱,但报价与实扣对不上账)。
  if (ENGINE_EXCLUDED_TEXT_TAGS.has(b.styleTag ?? "")) return false;
  // 一键模式(直达):全 A —— 所有非图块都过引擎(qr 已在上面排除)。
  if (ctx.oneClick) return true;
  const mode = b.canvas?.renderMode;
  if (mode === "A") return true;
  if (mode === "B") return !!ctx.hasStyleTokens;
  if (mode === "C") return false;
  return !!ctx.hasStyleTokens;
}

/**
 * ★`blockCallsEngine` 的继任者(LI-SCREENS-2.0 WP5)—— 叠一条原件屏零引擎口径:
 * `originalKeys` 非空(资质/证书图并入原件屏,PIL 程序化贴放)⇒ 恒零引擎,
 * **盖过** `blockCallsEngine` 对图块的判断(它是 image 原语时会误判为过引擎)。
 * 与 ai 侧同口径:`orchestrator._render_originals_screen_shot` 走的是零 gen 计费的
 * PIL 路径,不是 `_generate_image_block`(见 wp0-recon §1.7 / image_routing.py 头注)。
 */
function blockPassesEngine(b: CountableBlock, ctx: EngineContext = {}): boolean {
  if (originalKeysOf(b).length > 0) return false;
  return blockCallsEngine(b, ctx);
}

/**
 * ★一键出图报价的**块数区间**(ADV-LI-QUOTE-RANGE-1,2026-08-21)——
 * 与 ai `action_tools._LI_BLOCKS` **逐格相同**,契约锁见
 * `__contract__/oneClickQuoteRange.contract.test.ts`(另一半在 ai 侧同名判据)。
 *
 * ★为什么不再从骨架算:**骨架既不是上界也不是下界**。真账单实测(2026-08-21 测试机 16 单):
 * ```
 *   电商详情 骨架 8 块 → 真实 3/4/5/6/8 块(2100~5600)
 *   活动海报 骨架 6 块 → 真实 4/6/7 块  (2800~4900)   ← 骨架数被"上穿"
 *   企业介绍 骨架 7 块 → 真实 4/7 块    (2800~4900)
 *   榜  单   骨架 5 块 → 真实 3/4 块    (2100~2800)
 * ```
 * 块数**跟用户给的料走,不跟用途走** —— 同一用途今天就出现过 5 个不同的块数。
 * 旧实现拿「骨架 × 全 A」当按钮上那个数,四个用途**没有一个说得准**
 * (电商详情报 5600、真实最低 2100;活动海报报 3500、真实最高 4900 —— 两个方向都错)。
 */
/**
 * 没有用途时的**并集区间**(2026-08-26)—— 覆盖每一个用途,所以对任何一条来路都
 * **只会报高、不会报低**。
 *
 * ★为什么这条能成立、而「无用途就不给一键」那条决定可以退役:
 *   上面那段注释自己写着「块数**跟用户给的料走,不跟用途走**」——
 *   既然块数本来就不由用途决定,那「没有用途就报不出可信的价」这个前提在
 *   **区间报价**上线之后就不成立了。四个上墙用途的下限**完全相同**(都是 3 块),
 *   只有上限差 8~11;取并集 = 取最宽的那个上限,方向是保守的。
 * ★而低报正是本仓的病灶(视频台本图报 280 实扣 700、口播报 1200 实扣 7500),
 *   所以「报高不报低」是这里该站的一边。
 * ★**从表派生、不硬编码** [3,11]:将来加用途/改区间,这里自动跟上;
 *   写死一份就会在下一次改 goods 区间时悄悄漂掉。
 */
function unionBlockRange(): readonly [number, number] {
  const ranges = Object.values(ONECLICK_BLOCK_RANGE);
  return [Math.min(...ranges.map((r) => r[0])), Math.max(...ranges.map((r) => r[1]))];
}

const ONECLICK_BLOCK_RANGE: Record<LongImageUsage, readonly [number, number]> = {
  goods: [3, 11],
  corp: [3, 10],
  event: [3, 9],
  review: [3, 9],
  report: [3, 9],
  hire: [3, 9],
  menu: [3, 9],
  tutorial: [3, 9],
  rank: [3, 8],
  social: [3, 9],
};

/**
 * ★一屏最多装几段(与 ai `app/modules/poster/longimage/screens.py::SCREEN_MAX_SECTIONS`
 * 同一口径,改一边必须改另一边)。一键 lane 从 LI-SCREEN-1 起**按屏结算** ——
 * 相邻 ≤3 段合成一屏、每屏一次 gen,块数不再是计费粒度。
 *
 * ★这是**镜像常量**,不是各写各的:ai 那边的装箱器是真源头,这边只是把同一个数
 *   写进报价。它漂了的表现是按钮上的价与实扣差整整一倍,而两边都不会报错。
 */
const ONECLICK_SCREEN_MAX_SECTIONS = 3;

/**
 * 块数 → **屏数**(一键 lane 计费粒度)。
 *
 * ★最优装箱下界:每屏塞满 {@link ONECLICK_SCREEN_MAX_SECTIONS} 段。真装箱器除段数
 *   外还看行密度(ai `SCREEN_MAX_EFF`),密块最坏**一块一屏** —— 所以这个数是
 *   **屏数下界**,不是"屏数"。报价上界因此仍按块数走,不许拿它去除(见
 *   {@link longImageOneClickEstimate}):低报是本仓 P0 病灶。
 */
export function oneClickScreensOf(blockCount: number): number {
  return Math.max(1, Math.ceil(blockCount / ONECLICK_SCREEN_MAX_SECTIONS));
}

/**
 * 计费屏数估算的产物。
 *
 * · `exact=true`:与 ai 实扣**同源**的精确值(low===high),可以拿去开「报价即合同」的单;
 * · `exact=false`:排屏标注不全,`low` 只是屏数**下界**(⌈过引擎块数/3⌉),`high` 是过引擎块数
 *   (密块最坏一块一屏的上界,只给展示用)—— 这时**不开报价单**,走软着陆按 ai 实际屏数扣;
 * · `byBlock=true`:这张图不走屏渲染(横宽 / 骨架稿),low===high===过引擎块数。横宽按块结算
 *   (exact=true 照开单);骨架稿图块不下引擎不计费,exact=false **不开单**(0925 拍板)。
 */
export interface ScreensEstimate {
  low: number;
  high: number;
  exact: boolean;
  byBlock: boolean;
}

/** 块的生效 renderMode:v2 产物顶层优先,编辑器态回落 canvas 嵌套(同 annoOf 两态取数)。 */
function renderModeOf(b: CountableBlock): string | undefined {
  return b.renderMode ?? b.canvas?.renderMode;
}

/**
 * 「独立通路块」(不进屏、零引擎、**不计费**)—— 与 api `LongImageScreenCount.isStandalone`、
 * ai `screens.screens_of` 的 `standalone = {C 档} | engine_free_ids` 逐条对应:
 *   · 图块(IMG 档)恒进屏;只有**非一键** lane 的 photocards(ENGINE_FREE_IMAGE_TAGS)断出独立通路
 *     (一键 lane 的 photocards 会被 `force_all_a_canvas` 换成真出图样式,照常进屏计费);
 *   · qr 恒 C;显式 A 进屏;显式 B 无 styleTokens 降 C;显式 C;未指定且无 styleTokens → C。
 * ★这里**不**镜像 `ctx.oneClick` 的全 A 推档:那一步在 ai 规划阶段(longimage_plan)就写进了块的
 *   renderMode,编排器(与 api)只看块上的 renderMode —— 与 {@link blockCallsEngine} 的一键全 A
 *   是两个用途(那个锁的是一键规划产物的跨仓契约,本函数锁的是按屏结算)。
 */
function isStandaloneBlock(b: CountableBlock, ctx: EngineContext): boolean {
  const tag = b.styleTag ?? "";
  if (b.primitive === "image") return !ctx.oneClick && ENGINE_FREE_IMAGE_TAGS.has(tag);
  if (ENGINE_EXCLUDED_TEXT_TAGS.has(tag)) return true;
  const mode = renderModeOf(b);
  if (mode === "A") return false;
  if (mode === "B") return !ctx.hasStyleTokens;
  if (mode === "C") return true;
  return !ctx.hasStyleTokens;
}

/**
 * ★长图**计费屏数**(0925 收尾波 D1a/A23「长图按屏报价」)——
 * **与 api `src/main/java/com/gscq/media/LongImageScreenCount.java` 逐条同源,改一处必改另一处**
 * (两边都镜像 ai `screens.screens_of` / `_screens_by_annotation` / `orchestrator._effective_mode`
 * / `_screens_active`;共用样本见 `longImageEstimate.screens.test.ts` 与 api `LongImageScreenCountTest`)。
 *
 * 为什么是屏:ai 全 lane 早已**每屏一次引擎调用、每屏结算 1 单位**(0902 真单 5 块 → 2 屏,实扣 2 × 单价)。
 * 此前搭建器拿**过引擎块数**开报价单,绑单后 `QuoteSettlement` 照单兑现不重算 ⇒ 按块付钱、按屏出图,多收;
 * 本函数上一版又把 qr / photocards 每个独立块 +1 屏(ai 不收这部分钱),也是多报。
 *
 * 判据(与 api 逐条):
 *   · 横宽(`ctx.wide`)/ 骨架稿(`ctx.skeleton`)不走屏渲染 → `byBlock`,沿用按块({@link engineBlocksOf});
 *   · 隐藏块(`hidden`)跳过,不断屏;
 *   · 独立块({@link isStandaloneBlock})不计费,但**断屏**(前后同号也各算一屏);
 *   · 相邻同一 screenNo 的块合成一屏(只认相邻,乱序按序断屏);
 *   · 屏内任一块带原件(originalKeys)= PIL 贴放,整屏零引擎零计费;
 *   · 进屏块**全部**带排屏标注 → 精确屏数(`exact`);
 *   · 标注不全 → ai 走几何装箱(还看行密度,不复制那套算法 —— 抄一份就是漂移源),
 *     只给下界 ⌈过引擎进屏块数 / 3⌉(`exact=false`),**不开报价单**。
 * 已知偏差(同 api 头注):屏底裁字守卫命中时 ai 对该屏多 2 次调用,实扣可能略高于精确值;
 * 有报价单时按单封顶,方向是少收不多收。
 */
export function screensOf(
  blocks: readonly CountableBlock[],
  ctx: EngineContext = {}
): ScreensEstimate {
  if (ctx.skeleton) {
    // ★0925 拍板:骨架稿图块只画示意位、不下引擎不计费(ai `_skeleton_image_block`),
    //   按块开单就是多报 → exact=false ⇒ quoteQtyOf 返回 null,不开单、软着陆(与手工稿同形)。
    const n = engineBlocksOf(blocks, ctx);
    return { low: n, high: n, exact: false, byBlock: true };
  }
  if (ctx.wide) {
    const n = engineBlocksOf(blocks, ctx);
    return { low: n, high: n, exact: true, byBlock: true };
  }
  let grouped = 0;
  let groupedAnnotated = 0;
  let engineGrouped = 0; // 进屏且不是原件块 —— 保底估算的分子(与 api 同名)
  let screens = 0;
  let curNo: number | null = null;
  let inScreen = false;
  let curBillable = false;
  for (const b of blocks) {
    if (b.hidden === true) continue;
    if (isStandaloneBlock(b, ctx)) {
      // 独立块断屏(_screens_by_annotation 里 standalone 就地 flush,cur_no 复位)
      if (inScreen && curBillable) screens++;
      inScreen = false;
      curNo = null;
      continue;
    }
    grouped++;
    const original = originalKeysOf(b).length > 0;
    if (!original) engineGrouped++;
    const anno = annoOf(b);
    const no = typeof anno?.screenNo === "number" ? anno.screenNo : null;
    if (no != null) groupedAnnotated++;
    if (inScreen && no !== curNo) {
      if (curBillable) screens++;
      inScreen = false;
    }
    if (!inScreen) {
      inScreen = true;
      curBillable = true;
    }
    curNo = no;
    if (original) curBillable = false; // 原件屏:屏内任一块带原件 → 整屏零计费
  }
  if (inScreen && curBillable) screens++;
  if (grouped > 0 && groupedAnnotated === grouped) {
    return { low: screens, high: screens, exact: true, byBlock: false };
  }
  // 标注不全 → 只给下界(见头注);high = 过引擎进屏块数(密块最坏一块一屏,仅展示用)
  const floor = Math.ceil(engineGrouped / ONECLICK_SCREEN_MAX_SECTIONS);
  return { low: floor, high: engineGrouped, exact: false, byBlock: false };
}

/**
 * 长图**开报价单的数量**单一入口(搭建器所有开单路径只许走这里)。
 * 精确(按屏 / 横宽骨架按块)且 > 0 → 数量;标注不全(只有下界)或零计费 → `null` = 不开单,
 * 软着陆按 ai 实际屏数扣。★不许拿 `imageBlocks`(过引擎**块数**)开单 —— 那是「历史多收 37%」的主来源。
 */
export function quoteQtyOf(s: ScreensEstimate): number | null {
  return s.exact && s.high > 0 ? s.high : null;
}

/**
 * 一键出图按钮上那个价 —— **区间**,不是单值。
 *
 * ★区间宽是**如实**,不是没算清楚:真实跨度就是这么宽,所以按钮旁边必须同时说清
 *   「按实际屏数结算」。把它收窄成一个好看的数,就是本仓低报病灶的来路
 *   (视频台本图报 280 实扣 700、口播报 1200 实扣 7500 都是这么来的)。
 * ★积分**按屏算**(LI-QUOTE-SCREENS-1,2026-09-02):见下面 `screensLow/screensHigh`。
 *   `lowBlocks/highBlocks` 保留给「这张图大概几段」的展示用 —— 用户理解的是内容段数,
 *   计费粒度才是屏。两个量同名混用正是本仓「同名两个量」那一族的来路。
 * ★`skeletonBlocks` / `engineBlocks` 仍然返回,但**只给"这张图大概多长"用**,
 *   不参与报价 —— 旧名 `totalBlocks` 改成 `skeletonBlocks` 就是为了让调用处看见
 *   它是骨架数、不是真排数(这次的病根正是这两个被当成了一回事)。
 */
export function longImageOneClickEstimate(
  /** `null` = 这条来路没有用途(追爆蓝图 / 直接进)→ 走 {@link unionBlockRange} 的并集区间。 */
  usage: LongImageUsage | null,
  tier: ResolutionTier = ONECLICK_TIER as ResolutionTier,
  /**
   * 该档每块单价 —— **由调用方从价目表取**(`book.unit(SKU.longImage, imageVariant(provider, tier))`)。
   *
   * ★ 原来这里直接读 `IMAGE_CREDITS_BY_TIER`,那是前端手抄的价表。出图线之所以一直没出事,
   *   是因为只有它的新旧加价率都是 ×10;而这个函数**长得像在算真价**,谁都不会去查它的数据源。
   *   现在价从外面传进来,取不到就是 null,由调用方渲染骨架 —— 不许兜一个数。
   */
  perBlockCredits?: number | null
): {
  creditsLow: number | null;
  creditsHigh: number | null;
  lowBlocks: number;
  highBlocks: number;
  /** 屏数区间(计费粒度)。★段数展示仍用 `lowBlocks/highBlocks` —— 用户理解的是内容段数。 */
  screensLow: number;
  screensHigh: number;
  engineBlocks: number;
  skeletonBlocks: number;
  /** 该档每块单价(入参原样带回;价目表没到货时为 null)。 */
  perBlock: number | null;
  tier: ResolutionTier;
} {
  const [lowBlocks, highBlocks] = usage ? ONECLICK_BLOCK_RANGE[usage] : unionBlockRange();
  const unit = perBlockCredits ?? null;
  // 无用途 = 没有确定的骨架可言。这两个字段宁可给 0,也不拿某个用途的骨架冒充
  // (它们只服务于「这一套骨架长什么样」的展示,报价不经过它们)。
  const blocks = usage ? skeletonFor(usage) : [];
  // ★LI-QUOTE-SCREENS-1(2026-09-02):积分按**屏**算,不再按块 —— 实扣从 LI-SCREEN-1
  //   起就是每屏一次 gen,而这里一直还在拿块数乘单价,按钮上的价与实扣差整整一倍。
  // ★两头**不对称**是有意的,不是漏改:
  //   · 下沿按最优装箱(每屏塞满 3 段)—— 少承诺允许,如实降低;
  //   · 上界**不许**跟着除 3:真装箱器除段数还看行密度(ai `SCREEN_MAX_EFF`),
  //     密块最坏一块一屏 ⇒ 屏数上界 = 块数上界。除了它就是为一个不成立的最坏情形
  //     低报,而低报正是本仓 P0 病灶(0819 上界被真账单打穿过两次)。
  const screensLow = oneClickScreensOf(lowBlocks);
  const screensHigh = highBlocks;
  return {
    creditsLow: unit == null ? null : screensLow * unit,
    creditsHigh: unit == null ? null : screensHigh * unit,
    lowBlocks,
    highBlocks,
    screensLow,
    screensHigh,
    engineBlocks: engineBlocksOf(blocks, { oneClick: true }),
    skeletonBlocks: blocks.length,
    perBlock: unit,
    tier,
  };
}

/** @deprecated 用 engineBlocksOf —— 「是不是 image」不等于「会不会叫引擎」。 */
export const imageBlocksOf = engineBlocksOf;

/** 图块数 × 该档单张积分。单价由调用方从价目表取;取不到返 null(不兜数)。 */
export function creditsForImageBlocks(
  imageBlocks: number,
  perImageCredits: number | null
): number | null {
  return perImageCredits == null ? null : imageBlocks * perImageCredits;
}

export interface LongImageEstimate {
  blockCount: number;
  /** @deprecated 逐块口径,GenerateCockpit 展示不再用它 —— 见 screensLow/screensHigh 头注。 */
  imageBlocks: number;
  tier: ResolutionTier;
  /** @deprecated 逐块口径(imageBlocks × 单价),与实扣(按屏结算)系统性偏高。
   *  GenerateCockpit 展示改用 screensLow/screensHigh × 单价现算区间;此字段只留给
   *  尚未跟进的旧调用方读,不删是防外部 import 编译炸(同 blockCallsEngine 退役形制)。 */
  credits: number | null;
  /**
   * ★LI-SCREENS-2.0 WP5(2026-09-05 规格评审实锤修正):**按屏计费区间**,是
   * GenerateCockpit 价格展示的权威源。评审实锤:6 块合 2 屏的稿子,旧口径
   * (imageBlocks×单价)显示 396 分而实扣 132 分 —— 在用户出图决策的最后一屏
   * 系统性高估达 3 倍。`screensOf(blocks, ctx)` 同一个函数(与 PrepareOutlineCard
   * 脚部同款改法),带标注→精确值(low===high);无标注→区间(不模拟 ai 装箱算法)。
   * ★0925 A23:开单数量已改走 {@link quoteQty}(按屏精确值),不再用 imageBlocks。
   */
  screensLow: number;
  screensHigh: number;
  /** true = screensLow===screensHigh 是与 ai 实扣同源的精确值;false = 只有下界(标注不全)。 */
  screensExact: boolean;
  /** true = 横宽/骨架不走屏渲染,screensLow/High 是按块数(文案要说「块」不说「屏」)。 */
  billByBlock: boolean;
  /**
   * ★开报价单的数量(0925 A23):{@link quoteQtyOf}(screensOf(...))。null = 不开单
   * (标注不全只有下界 / 零计费)→ 软着陆按 ai 实际屏数扣。**任何开单路径只许用它**。
   */
  quoteQty: number | null;
}

/**
 * 页内实时预估:跟着用户增删块 + 换清晰度档 + 有无统一 DNA 走。
 *
 * @param perImageCredits 该档单张积分,由调用方从价目表取(`book.unit`);null = 还没到货
 */
export function estimateForBlocks(
  blocks: readonly CountableBlock[],
  scale = 2,
  ctx: EngineContext = {},
  perImageCredits: number | null = null,
  /**
   * 屏数用的上下文(缺省 = ctx)。一键合屏稿「改这一稿」要带 `oneClick` 给屏数判据
   * (photocards 进屏),但 `imageBlocks` 仍按老谓词算、不被一键全 A 推高 —— 两个量分开传。
   */
  screenCtx: EngineContext = ctx
): LongImageEstimate {
  const tier = tierOfScale(scale);
  const imageBlocks = engineBlocksOf(blocks, ctx);
  const screens = screensOf(blocks, screenCtx);
  return {
    blockCount: blocks.length,
    imageBlocks,
    tier,
    credits: creditsForImageBlocks(imageBlocks, perImageCredits),
    screensLow: screens.low,
    screensHigh: screens.high,
    screensExact: screens.exact,
    billByBlock: screens.byBlock,
    quoteQty: quoteQtyOf(screens),
  };
}

/**
 * 骨架预估,卡面标用 —— 骨架固定,故**块数/图块数**编译期已知。
 *
 * ★ 积分**不再**在这里算:它需要单价,而单价只有运行时才知道(价目表)。
 *   这就是为什么 `SceneCard.costLabel` 改成了函数 —— 卡面文案必须等到有价才成句。
 */
export interface SkeletonEstimate {
  blockCount: number;
  imageBlocks: number;
}

function skeletonEstimateOf(blocks: readonly CountableBlock[]): SkeletonEstimate {
  return { blockCount: blocks.length, imageBlocks: engineBlocksOf(blocks) };
}

/**
 * 10 用途骨架的默认块数 / 图块数 / 默认档积分。
 * ★遍历 `LONG_IMAGE_USAGES` 生成 —— 加一个用途这里自动跟上,不会漏。
 */
export const SKELETON_ESTIMATE: Record<LongImageUsage, SkeletonEstimate> = Object.fromEntries(
  LONG_IMAGE_USAGES.map((usage) => [usage, skeletonEstimateOf(skeletonFor(usage))])
) as Record<LongImageUsage, SkeletonEstimate>;

/** 横宽 4 版式骨架(卡面用默认版式 `lr`)。 */
export const WIDE_SKELETON_ESTIMATE: Record<WideLayoutKey, SkeletonEstimate> = Object.fromEntries(
  WIDE_LAYOUT_KEYS.map((layout) => [layout, skeletonEstimateOf(wideSkeletonFor(layout))])
) as Record<WideLayoutKey, SkeletonEstimate>;
